import {
  COMPANY_CTA_FEAT_FLAG,
  FEATURE_TABLES_SPLIT_NAME,
  TRIAL_RECOMMENDATIONS_FLAG,
} from "@/constants";
import { isEqual } from "lodash";
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

/**
 * Treatment values expected to be used in the app
 */
type TreatmentValue = boolean | null;

/**
 * Map of feature flags in use.
 * [split_name]: treatment.
 * Update this type to add/remove treaments/flags.
 */
export type Treatments =
  | {
      [COMPANY_CTA_FEAT_FLAG]: TreatmentValue;
      [FEATURE_TABLES_SPLIT_NAME]: TreatmentValue;
      [TRIAL_RECOMMENDATIONS_FLAG]: TreatmentValue;
    }
  | Record<string, never>;

export const defaultTreatments: Treatments = {
  [COMPANY_CTA_FEAT_FLAG]: null,
  [FEATURE_TABLES_SPLIT_NAME]: null,
  [TRIAL_RECOMMENDATIONS_FLAG]: null,
};

type TreatmentsContextValue = {
  treatments: Treatments;
  setTreatments: Dispatch<SetStateAction<Treatments>>;
};

const TreatmentsContext = createContext<TreatmentsContextValue>({
  treatments: defaultTreatments,
  setTreatments: () => {
    /* noop */
  },
});

export function useTreatmentsContext() {
  return useContext(TreatmentsContext);
}

type Props = {
  children: ReactNode;
  initialValue: Treatments;
};
export function TreatmentsProvider({ children, initialValue }: Props) {
  const [treatments, _setTreatments] = useState<Treatments>({
    ...defaultTreatments,
    ...(initialValue ? initialValue : {}),
  });

  const prevTreatmentsRef = useRef<Treatments>(treatments);

  const setTreatments: Dispatch<SetStateAction<Treatments>> = useCallback(
    (action) => {
      _setTreatments((prevTreatments) => {
        if (typeof action === "function") {
          return action(prevTreatments);
        }
        return action;
      });
    },
    []
  );

  useEffect(() => {
    if (!isEqual(treatments, prevTreatmentsRef.current)) {
      _setTreatments(treatments);
      prevTreatmentsRef.current = treatments;
    }
  }, [treatments]);

  return (
    <TreatmentsContext.Provider value={{ treatments, setTreatments }}>
      {children}
    </TreatmentsContext.Provider>
  );
}
